import {useLocation} from "react-router-dom";
import React, {useEffect, useRef, useState} from "react";
import ChessBoard from "./ChessBoard";
import {Chess, DEFAULT_POSITION} from "chess.js";
import {fetchAnalysis, fetchSingleMoveAnalysis} from "../clients/GameClient";
import Moves from "./Moves";
import EvalBar from "./EvalBar";
import Spinner from "./svg/Spinner";
import {useUser} from "../UserContext";
import AnalysisChart from "./AnalysisChart";
import AnalysisOverview from "./AnalysisOverview";
import {rateMoves} from "../utils/analysisFunctions";
import UserLink from "./UserLink";

const Analysis = () => {
    const location = useLocation()
    const state = location.state
    const [orientation, setOrientation] = useState(state.playerColor || "white")
    const moves = state.moves;
    const divergingMoves = useRef([])
    const gameId = state.gameId;
    const moveNumber = useRef(0);
    const user = useUser();
    const opponent = state.opponent;


    const initialPosition = state.initialFen || DEFAULT_POSITION
    //This may seem a bit like "double work", but the game object is for easily moving back and forth in moves, while
    //fen is in order to properly rerender the chessboard.
    const game = useRef(new Chess(initialPosition))
    const [fen, setFen] = useState(initialPosition)

    //This is a list of objects, that contain the eval and best moves
    // at any given point [{eval: XX, moves: [move1, move2]}, etc..]
    const [analysis, setAnalysis] = useState([])

    //We could optimize the divergence. But for now, diverging moves are treated as a simple stack, where if we
    //have elements on the stack, they always take priority - both in analysis and simple move lists
    const divergingMoveAnalysis = useRef([])
    const [analysisEnabled, setAnalysisEnabled] = useState(false)
    const evalRegex = /cp (-*\d*)/

    //These are used to make sure to properly rerender evalBar and best move sequence
    const [currentEval, setCurrentEval] = useState(null);
    const [currentBestMoves, setCurrentBestMoves] = useState([0])
    const [fetchingAnalysis, setFetchingAnalysis] = useState(false)

    const [moveRatingCategories, setMoveRatingCategories] = useState([])
    const divergingMoveRatingCategories = useRef([])
    //This keeps track of which move symbol (best move, blunder etc.) should be displayed on the board, and on which square
    //Thus, the object has the structure {symbol: <SomeSymbol/>, square: "e7"}
    const [currentMoveSymbol, setCurrentMoveSymbol] = useState(null)

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowRight") {
                nextMove()
            }

            if (e.key === "ArrowLeft") {
                previousMove();
            }
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [analysisEnabled]);

    //This method is used as a callback in the chessboard, when the user decides to make a manual move.
    //Therefore, we are diverging from the "played" path
    const handleMove = (newFen, move) => {

        //we retrieve the move from here - because we know the move format from game is very consistent (our own is not always)
        const formattedMove = game.current.move(move);
        setFen(game.current.fen());
        divergingMoves.current.push(formattedMove.san);

        if (analysisEnabled) {
            analyseSingleMove();
        }
    }

    const updateCurrentMoveSymbol = () => {
        let moveSymbol = null
        const movesMade = game.current.history().length;

        if(movesMade === 0) {
            setCurrentMoveSymbol(null);
            return
        }

        //if we are on a diverging path, that always takes priority
        if(divergingMoveRatingCategories.current.length > 0) {
            moveSymbol = divergingMoveRatingCategories.current.at(-1)
        } else {
            moveSymbol = moveRatingCategories[movesMade - 1]
        }

        const square = game.current.history({verbose: true}).at(-1).to;
        setCurrentMoveSymbol({symbol: moveSymbol.category.symbol, square: square})
    }

    const goBackToMoveIndex = (index) => {
        if(index < 0) {
            console.error("Cannot go to negative move index")
            return
        }

        while(moveNumber.current > index) {
            game.current.undo();
            moveNumber.current--;
        }
    }

    const goForwardToMoveIndex = (index) => {

        if(index > moves.length) {
            console.error("Cannot go to index that is outside length of game")
            return
        }

        while(index > moveNumber.current) {
            game.current.move(moves[moveNumber.current]);
            moveNumber.current++;
        }
    }

    const goToMoveIndexAndUpdateState = (index) => {
        if(index >= moveNumber.current) {
            goForwardToMoveIndex(index)
        } else {
            goBackToMoveIndex(index)
        }

        setFen(game.current.fen())
        if (analysisEnabled) {
            updateEvalAndMoves(getCurrentAnalysisRow())
            updateCurrentMoveSymbol()
        }
    }

    const nextMove = () => {
        //We can only automatically give the next move when you are still along the "played" path
        if (moveNumber.current < moves.length && divergingMoves.current.length === 0) {
            game.current.move(moves[moveNumber.current]);

            moveNumber.current++;
            setFen(game.current.fen())
            if (analysisEnabled) {
                updateEvalAndMoves(getCurrentAnalysisRow())
                updateCurrentMoveSymbol()
            }
        }
    }

    const previousMove = () => {
        if (divergingMoves.current.length === 0 && moveNumber.current === 0) {
            return
        }

        if (divergingMoves.current.length > 0) {
            divergingMoves.current.pop();
            divergingMoveAnalysis.current.pop()
            divergingMoveRatingCategories.current.pop()
        } else if (moveNumber.current > 0) {
            moveNumber.current--;
        }

        game.current.undo();
        setFen(game.current.fen())
        if (analysisEnabled) {
            updateEvalAndMoves(getCurrentAnalysisRow())
            updateCurrentMoveSymbol()
        }
    }

    // Flip board orientation
    const toggleOrientation = () => {
        setOrientation((prev) => (prev === "white" ? "black" : "white"));
    };

    const parseAnalysisRow = (row) => {
        if (row.eval.includes("cp")) {
            //It will be a centipawn eval diff, convert this
            let advantage = parseInt(row.eval.match(evalRegex)[1])
            row.eval = advantage / 100
        }
    }

    const updateEvalAndMoves = (analysisRow) => {
        setCurrentEval(analysisRow.eval);
        setCurrentBestMoves(analysisRow.moves);
    }

    const isDefaultOrientation = () => {
        return orientation === state.playerColor;
    }

    const toggleAnalysis = () => {

        if (analysisEnabled) {
            setAnalysisEnabled(prevState => !prevState)
            return
        }

        setFetchingAnalysis(true)
        //This is "primitive caching" to ensure we dont always call backend when toggling back and forth
        if (analysis.length === 0) {
            fetchAnalysis(gameId)
                .then((data) => {
                    data.forEach((row) => {
                        parseAnalysisRow(row)
                    });
                    setAnalysis(data)
                    setAnalysisEnabled(true)
                    updateEvalAndMoves(data[0])
                    setFetchingAnalysis(false)
                    setMoveRatingCategories(rateMoves(data.map((row) => row.eval), data.map((row) => row.turn)))
                })
        } else {
            setAnalysisEnabled(true)
            setFetchingAnalysis(false)
        }

    }

    const analyseSingleMove = () => {
        fetchSingleMoveAnalysis(initialPosition, game.current.history())
            .then(data => {
                data.forEach(row => {
                    parseAnalysisRow(row);
                    //method is generally not used for single moves, so returns an array - but we can simply take the arrays only element
                    divergingMoveRatingCategories.current.push(
                        rateMoves([getCurrentAnalysisRow().eval, row.eval], [getCurrentAnalysisRow().turn, row.turn])[0])
                    divergingMoveAnalysis.current.push(row);
                });
                updateEvalAndMoves(data[0])
                updateCurrentMoveSymbol()
            })
    }


    const getCurrentAnalysisRow = () => {
        if (divergingMoveAnalysis.current.length > 0) {
            return divergingMoveAnalysis.current.at(-1)
        } else {
            return analysis[moveNumber.current]
        }
    }

    const BestMoveList = ({moves}) => {
        return <div style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginLeft: "1rem",
            marginRight: "0.5rem",
            overflow: "hidden",
            maxWidth: "100%",
            marginTop: "0.5rem"
        }}>
            <p style={{
                color: "#666",
                height: "1rem",
                margin: "0.2rem",
                fontSize: "0.8rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            }}>
                Best Line: {moves.map(move => move + " ")}</p>
        </div>
    }

    return (
        <div style={{display: "flex", justifyContent: "center"}}>
            <div style={{
                display: "flex",
                flexDirection: "column",
                marginTop: '2rem',
                width: "fit-content"
            }}>
                <button className="minimalist-button"
                        style={{
                            alignSelf: "flex-end",
                            margin: "0.5rem",
                            marginRight: "1rem",
                            display: "flex",
                            alignItems: "center"
                        }}
                        onClick={toggleAnalysis}><span
                    style={{marginRight: "0.5rem"}}>Enable Analysis</span> {analysisEnabled ?
                    <img src={"/checked_box.png"} alt={"Checked Box"}/> :
                    <img src={"/empty_box.png"} alt={"Empty Box"}/>}
                </button>
                <div style={{display: "flex", flexDirection: "row", height: "28.1rem", position: "relative"}}>
                    {analysisEnabled && <div style={{width: "20rem", display: "flex", flexDirection: "column"}}>
                        <AnalysisOverview whiteMoveRatings={moveRatingCategories.filter(rating => rating.color === "w")}
                                          blackMoveRatings={moveRatingCategories.filter(rating => rating.color === "b")}/>
                        <div style={{marginLeft: "-1rem"}}>
                            <AnalysisChart data={analysis.map((analysisRow) => analysisRow.eval)} onPointClick={goToMoveIndexAndUpdateState}/>
                        </div>
                    </div>}
                    {fetchingAnalysis &&
                        <div style={{height: "100%", width: "65%", zIndex: "999", position: "absolute", left: "2rem", top: "-1.3rem"}}>
                            <Spinner color="#888"/>
                        </div>
                    }
                    <div
                        style={{minWidth: "1.5rem", height: "100%", margin: "1rem"}}>{analysisEnabled &&
                        <EvalBar eval={currentEval} orientation={orientation}/>}</div>
                    <div style={{position: "relative", fontFamily: "monospace", fontSize: "0.9rem", color: "#666"}}>
                        <UserLink user={isDefaultOrientation() ? opponent : user} style={{position: "absolute", top: "-2.5rem", right: "0"}}/>
                        <ChessBoard isLiveGame={false} moveCallback={handleMove} fen={fen} orientation={orientation} customSymbol={currentMoveSymbol}/>
                        <UserLink user={isDefaultOrientation() ? user : opponent} style={{position: "absolute", right: "0", bottom: "-2.5rem"}}/>
                    </div>
                    <div style={{display: "flex", flexDirection: "column", height: "100%", width: "12rem", marginLeft: "2rem"}}>
                        <Moves moves={[...moves.slice(0, moveNumber.current), ...divergingMoves.current]}
                               initialFen={state.initialFen}/>
                        {analysisEnabled && <BestMoveList moves={currentBestMoves}/>}
                    </div>

                <img
                    src="swap_symbol.png"
                    alt="Swap Board Orientation"
                    className="icon-style"
                    onClick={toggleOrientation}
                />
            </div>
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                marginRight: "0.5rem",
                marginTop: "2rem",
                marginLeft: "2rem"
            }}>
                <button className="minimalist-button" onClick={previousMove}
                        style={{display: "flex", alignItems: "center", outline: "none"}}><img
                    src={"/arrow_left.png"}
                    alt={"Left Arrow"}/> Previous Move
                </button>
                <button className="minimalist-button" onClick={nextMove}
                        style={{display: "flex", alignItems: "center", outline: "none"}}>Next Move <img
                    src={"/arrow_right.png"}
                    alt={"Right Arrow"}/>
                </button>
            </div>

        </div>
</div>

)
}

export default Analysis